import React, { useState, useMemo } from 'react';
import { Tag, Layers, Droplets, Sun, Cloud, Thermometer, AlertTriangle, Home, TrendingUp, Moon, Info, Clock, Archive, Camera, Box, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LegendSection = ({ heading, items, testid }) => {
  if (items.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={testid}>
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(125, 12%, 32%)' }}>
        {heading}
      </h4>
      <dl className="space-y-1.5">
        {items.map((it) => (
          <div key={it.label} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">{it.icon}</span>
            <div className="min-w-0">
              <dt className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>{it.label}</dt>
              <dd className="text-xs leading-snug" style={{ color: 'hsl(125, 12%, 32%)' }}>{it.hint}</dd>
            </div>
          </div>
        ))}
      </dl>
    </div>
  );
};

/**
 * A one-tap glossary of every icon used across the app's care details
 * and library UI. Rendered both inside the PlantDetailModal (via a small
 * `?` button) and as a section inside the SettingsModal.
 *
 * Includes a client-side filter box so power users with long care checklists
 * can jump straight to a specific icon.
 */
export function IconLegend() {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');

  const CARE = [
    { icon: <Tag className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('family') || 'Family', hint: t('legendFamily') || 'Botanical family (e.g. Araceae, Cactaceae).' },
    { icon: <Layers className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('plantType') || 'Plant type', hint: t('legendPlantType') || 'Growth form: succulent, vine, herbaceous perennial, etc.' },
    { icon: <Droplets className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('watering') || 'Watering', hint: t('legendWatering') || 'How often and how much to water this plant.' },
    { icon: <Sun className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('light') || 'Light', hint: t('legendLight') || 'Preferred light level, from low to full sun.' },
    { icon: <Cloud className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('humidity') || 'Humidity', hint: t('legendHumidity') || 'Humidity range the plant is happy in.' },
    { icon: <Thermometer className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('temperature') || 'Temperature', hint: t('legendTemperature') || 'Ideal temperature range in °F and °C.' },
    { icon: <AlertTriangle className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('toxicity') || 'Toxicity', hint: t('legendToxicity') || 'Whether it is toxic to pets, humans, or reptiles.' },
    { icon: <Home className="w-4 h-4" style={{ color: 'hsl(143, 28%, 45%)' }} aria-hidden="true" />, label: t('environment') || 'Environment', hint: t('legendEnvironment') || 'Standard household, terrarium preferred, or terrarium required.' },
    { icon: <TrendingUp className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('growthRate') || 'Growth rate', hint: t('legendGrowthRate') || 'Slow, moderate, or fast grower.' },
    { icon: <Moon className="w-4 h-4" style={{ color: 'hsl(230, 40%, 55%)' }} aria-hidden="true" />, label: t('dormancy') || 'Dormancy', hint: t('legendDormancy') || 'Fall/winter rest period — expect leaf drop or slower growth.' },
    { icon: <Info className="w-4 h-4" style={{ color: 'hsl(40, 50%, 45%)' }} aria-hidden="true" />, label: t('aiCareNote') || 'AI note', hint: t('legendAiNote') || 'AI-generated care information — verify against trusted sources.' },
  ];

  const LIBRARY = [
    { icon: <Clock className="w-4 h-4" style={{ color: 'hsl(40, 50%, 45%)' }} aria-hidden="true" />, label: t('legendIncomplete') || 'Complete later', hint: t('legendIncompleteHint') || 'Plant is saved but missing photos or care details.' },
    { icon: <Camera className="w-4 h-4" style={{ color: 'hsl(143, 28%, 45%)' }} aria-hidden="true" />, label: t('legendPhoto') || 'Add / replace photo', hint: t('legendPhotoHint') || 'Attach up to 5 photos per plant.' },
    { icon: <Archive className="w-4 h-4" style={{ color: 'hsl(21, 70%, 57%)' }} aria-hidden="true" />, label: t('legendArchive') || 'Archive', hint: t('legendArchiveHint') || 'Move a plant off the main library without deleting it.' },
    { icon: <Box className="w-4 h-4" style={{ color: 'hsl(143, 28%, 45%)' }} aria-hidden="true" />, label: t('legendTerrarium') || 'Terrarium filter', hint: t('legendTerrariumHint') || 'Show only plants that need or prefer a terrarium.' },
    { icon: <Moon className="w-4 h-4" style={{ color: 'hsl(230, 40%, 55%)' }} aria-hidden="true" />, label: t('legendDormant') || 'Dormant filter', hint: t('legendDormantHint') || 'Show only plants currently in their dormancy window.' },
  ];

  const q = query.trim().toLowerCase();
  const matches = (it) => !q || it.label.toLowerCase().includes(q) || it.hint.toLowerCase().includes(q);
  const filteredCare = useMemo(() => CARE.filter(matches), [q, CARE]);
  const filteredLibrary = useMemo(() => LIBRARY.filter(matches), [q, LIBRARY]);
  const nothingFound = q && filteredCare.length === 0 && filteredLibrary.length === 0;

  return (
    <div className="space-y-3" data-testid="icon-legend">
      {/* Search box — auto-focus is intentionally NOT enabled so opening the
          popover on mobile doesn't yank up the keyboard when the user only
          wanted to skim the list. */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: 'hsl(125, 12%, 45%)' }} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('legendSearchPlaceholder') || 'Search icons…'}
          className="w-full pl-8 pr-2 py-1.5 text-xs rounded-md border focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'white',
            borderColor: 'hsl(143, 28%, 88%)',
            color: 'hsl(143, 28%, 20%)',
            '--tw-ring-color': 'hsl(143, 28%, 45%)',
          }}
          aria-label={t('legendSearchPlaceholder') || 'Search icons'}
          data-testid="icon-legend-search"
        />
      </div>

      {nothingFound ? (
        <p className="text-xs italic py-2" style={{ color: 'hsl(125, 12%, 45%)' }} data-testid="icon-legend-empty">
          {t('legendNoResults') || 'No icons match your search.'}
        </p>
      ) : (
        <div className="space-y-4">
          <LegendSection
            heading={t('legendCareSection') || 'Care details'}
            items={filteredCare}
            testid="icon-legend-care"
          />
          <LegendSection
            heading={t('legendLibrarySection') || 'Library controls'}
            items={filteredLibrary}
            testid="icon-legend-library"
          />
        </div>
      )}
    </div>
  );
}
